import { useState, useEffect } from "react";

interface UseFallbackProductImageOptions {
  /** Whether this is for a brand or vendor */
  type: "brand" | "vendor";
  /** The brand or vendor ID */
  id?: number;
  /** The brand or vendor name/label */
  label?: string;
  /** URL of the primary product image */
  primaryImageUrl?: string | null;
  /** Whether the primary image failed to load */
  primaryImageError: boolean;
  /** Whether fallback images are enabled */
  enabled: boolean;
}

/**
 * Hook for fetching fallback images when primary images fail.
 *
 * Fetches a fallback image from the backend when the primary image
 * errors or is missing. Can be disabled via environment variable.
 *
 * @param options - Configuration options
 * @returns Fallback image URL or null
 *
 * @example
 * ```tsx
 * function ProductImage({ brand, imageUrl, onImageError }) {
 *   const [imageError, setImageError] = useState(false);
 *
 *   const fallbackUrl = useFallbackProductImage({
 *     type: "brand",
 *     id: brand.id,
 *     label: brand.name,
 *     primaryImageUrl: imageUrl,
 *     primaryImageError: imageError,
 *     enabled: true,
 *   });
 *
 *   const src = imageError && fallbackUrl ? fallbackUrl : imageUrl;
 *
 *   return (
 *     <img
 *       src={src}
 *       alt={brand.name}
 *       onError={() => setImageError(true)}
 *     />
 *   );
 * }
 * ```
 */
export const useFallbackProductImage = ({
  type,
  id,
  label,
  primaryImageUrl,
  primaryImageError,
  enabled,
}: UseFallbackProductImageOptions) => {
  const [fallbackImage, setFallbackImage] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !id || !label) {
      setFallbackImage(null);
      return;
    }

    if (primaryImageUrl && !primaryImageError) {
      setFallbackImage(null);
      return;
    }

    const fetchFallbackImage = async () => {
      try {
        const params = new URLSearchParams({
          type,
          id: String(id),
          label,
        });

        const response = await fetch(`/api/fallback-image?${params}`);
        const data = await response.json();
        setFallbackImage(data.image || null);
      } catch {
        setFallbackImage(null);
      }
    };
    if (process.env.NEXT_PUBLIC_DISABLE_FALLBACK_IMAGES === "true") {
      return;
    }

    fetchFallbackImage();
  }, [enabled, id, label, type, primaryImageUrl, primaryImageError]);

  return fallbackImage;
};
